import { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim() || formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.subject.trim() || formData.subject.length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.', {
        icon: <CheckCircle className="w-5 h-5 text-green-500" />
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.', {
        icon: <AlertCircle className="w-5 h-5 text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Our Location',
      content: 'Sawariya Global Exports LLP\nPipli Road, District Kurukshetra\nHaryana - 136132, India',
      link: null
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+91-9896557781',
      link: 'tel:+919896557781'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'rudraksh@sawariyaglobalexports.com',
      link: 'mailto:rudraksh@sawariyaglobalexports.com'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon - Sat: 9:00 AM - 6:00 PM\nSunday: Closed',
      link: null
    }
  ];

  return (
    <main data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section
        data-testid="contact-hero"
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1662142272211-4dd638b7bd5f?w=1920')`
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-24 pb-12 text-center">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block animate-fade-up">
            Get in Touch
          </span>
          <h1
            data-testid="contact-title"
            className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-up delay-100"
          >
            Contact <span className="text-gold">Us</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-white/80 max-w-2xl mx-auto animate-fade-up delay-200">
            Ready to source premium basmati rice? Let's discuss your requirements
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-form-section" className="py-24 md:py-32 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="mb-8">
                <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                  Reach Out
                </span>
                <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
                  Let's Start a Conversation
                </h2>
                <div className="gold-line bg-gold mb-6" />
                <p className="font-body text-gray-600">
                  Whether you're looking for bulk orders, private labelling, or have any 
                  questions about our products, our team is here to help.
                </p>
              </div>

              {contactInfo.map((item, index) => (
                <div
                  key={index}
                  data-testid={`contact-info-${index}`}
                  className="bg-white p-6 flex gap-4 group hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="w-12 h-12 bg-cream flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                    <item.icon className="w-6 h-6 text-navy group-hover:text-gold transition-colors" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-navy mb-1">
                      {item.title}
                    </h4>
                    {item.link ? (
                      <a
                        href={item.link}
                        className="font-body text-gray-600 hover:text-gold transition-colors whitespace-pre-line"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <p className="font-body text-gray-600 whitespace-pre-line">
                        {item.content}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 md:p-12 shadow-lg">
                <h3 className="font-heading text-2xl font-semibold text-navy mb-6">
                  Send Us a Message
                </h3>

                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label className="block font-body text-sm tracking-widest uppercase text-gray-600 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className={`w-full px-4 py-3 bg-cream border-0 font-body focus:ring-2 focus:ring-gold outline-none transition-all ${
                          errors.name ? 'ring-2 ring-red-400' : ''
                        }`}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-500 font-body">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block font-body text-sm tracking-widest uppercase text-gray-600 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className={`w-full px-4 py-3 bg-cream border-0 font-body focus:ring-2 focus:ring-gold outline-none transition-all ${
                          errors.email ? 'ring-2 ring-red-400' : ''
                        }`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500 font-body">{errors.email}</p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block font-body text-sm tracking-widest uppercase text-gray-600 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        data-testid="input-company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Ltd."
                        className="w-full px-4 py-3 bg-cream border-0 font-body focus:ring-2 focus:ring-gold outline-none transition-all"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block font-body text-sm tracking-widest uppercase text-gray-600 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 8900"
                        className="w-full px-4 py-3 bg-cream border-0 font-body focus:ring-2 focus:ring-gold outline-none transition-all"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block font-body text-sm tracking-widest uppercase text-gray-600 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      data-testid="input-subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Inquiry about bulk orders"
                      className={`w-full px-4 py-3 bg-cream border-0 font-body focus:ring-2 focus:ring-gold outline-none transition-all ${
                        errors.subject ? 'ring-2 ring-red-400' : ''
                      }`}
                    />
                    {errors.subject && (
                      <p className="mt-1 text-sm text-red-500 font-body">{errors.subject}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block font-body text-sm tracking-widest uppercase text-gray-600 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Tell us about your requirements..."
                      className={`w-full px-4 py-3 bg-cream border-0 font-body focus:ring-2 focus:ring-gold outline-none transition-all resize-none ${
                        errors.message ? 'ring-2 ring-red-400' : ''
                      }`}
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-500 font-body">{errors.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="submit-btn"
                    disabled={loading}
                    className="w-full md:w-auto px-12 py-4 bg-navy text-white font-body text-sm tracking-widest uppercase flex items-center justify-center gap-3 hover:bg-navy-dark transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section data-testid="map-section" className="py-24 md:py-32 diagonal-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center mb-12">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Our Location
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Find Us in <span className="text-gold">Haryana</span>
            </h2>
            <div className="gold-line bg-gold mx-auto" />
          </div>

          <div className="relative h-96 bg-cream overflow-hidden">
            {/* Placeholder Map Image */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-16 h-16 text-navy/30 mx-auto mb-4" />
                <p className="font-body text-gray-500">
                  Sawariya Global Exports LLP<br />
                  Haryana, India
                </p>
              </div>
            </div>
            {/* Decorative overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-cream to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Quick CTA */}
      <section data-testid="contact-cta" className="py-16 bg-navy">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-2">
                Need Immediate Assistance?
              </h3>
              <p className="font-body text-white/70">
                Our team is available during business hours to answer your queries
              </p>
            </div>
            <a
              href="tel:+911234567890"
              data-testid="quick-call-btn"
              className="inline-flex items-center gap-3 bg-gold text-navy-dark px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-gold-light transition-colors"
            >
              <Phone size={18} />
              Call Now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
